/**
 * FP3-049 — Reusable password input with a per-field show/hide
 * toggle.
 *
 * Wraps the shared shadcn `<Input>` so existing styling, focus
 * rings, and disabled states stay identical. The toggle button is
 * absolutely positioned inside the field on the right edge,
 * matching the calm Frame Practice aesthetic — no second column,
 * no layout shift.
 *
 * Security
 *   - The toggle ONLY flips the input's `type` attribute between
 *     "password" and "text". The value never leaves the React
 *     state — we never console.log it, never log it server-side
 *     differently, and never persist plaintext.
 *   - The toggle defaults to hidden on mount. There's no opt-out
 *     for "remember my preference" — that would be a regression
 *     for shared / kiosk machines.
 *
 * Usage
 *   <PasswordInput
 *     id="login-password"
 *     data-testid="login-password"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *     autoComplete="current-password"
 *   />
 *
 * Any other `<Input>` prop (className, placeholder, autoComplete,
 * required, etc.) is forwarded transparently.
 */
import { forwardRef, useState } from "react";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

const PasswordInput = forwardRef(function PasswordInput(
  {
    className = "",
    visibilityToggleTestId,
    "data-testid": dataTestId,
    ...rest
  },
  ref
) {
  const [visible, setVisible] = useState(false);

  // Derive a default testid for the toggle from the parent field's
  // testid so test selectors stay predictable without callers
  // having to spell out the toggle id every time.
  const toggleTestId =
    visibilityToggleTestId ||
    (dataTestId ? `${dataTestId}-visibility-toggle` : "password-visibility-toggle");

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        data-testid={dataTestId}
        // pr-10 keeps the typed value clear of the toggle button.
        className={`pr-10 ${className}`.trim()}
        {...rest}
      />
      <button
        type="button"
        data-testid={toggleTestId}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 flex items-center justify-center w-10 text-[#6B7280] hover:text-[#1E3F33] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3F33]/30 focus-visible:ring-offset-0 rounded-r-md transition-colors"
        tabIndex={0}
      >
        {visible ? (
          <EyeSlash size={16} weight="duotone" />
        ) : (
          <Eye size={16} weight="duotone" />
        )}
      </button>
    </div>
  );
});

export default PasswordInput;
